import React, { memo } from 'react';
import { ArrowRight, Clock3, MapPin } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { UpcomingEvent } from '../services/api';
import { SEMANA_CAFE, buildCampaignRows } from '../config/featuredEvents';

interface SemanaCafeSectionProps { events: UpcomingEvent[]; }
interface CampaignRow {
  eventId: string;
  eventUrl: string;
  eventName: string;
  name: string;
  dateLabel: string;
  date: string;
  venue: string;
  lineup: string[];
  mark: 'pre' | 'palmeira' | 'combo';
  accent: 'coral' | 'mint' | 'amarelo' | 'azul';
  cta: string;
  priceFrom: string | null;
  lotLabel: string | null;
}
const ease = [.22, 1, .36, 1] as const;

function Mark({ kind }: { kind: 'pre' | 'palmeira' }) {
  return <img className="semana-cafe__mark" src={SEMANA_CAFE.marks[kind]} alt="" aria-hidden="true" />;
}

function ComboPasses() {
  const reduced = useReducedMotion();
  return <motion.div className="semana-cafe__passes" aria-hidden="true" initial={reduced ? false : 'apart'} whileInView="joined" viewport={{ once: true, amount: .6 }}>
    <motion.span className="semana-cafe__pass semana-cafe__pass--first" variants={{ apart: { x: -12, rotate: -20 }, joined: { x: 0, rotate: -9 } }} transition={{ duration: reduced ? 0 : .8, ease }}>
      <Mark kind="pre" /><strong>29</strong><span>DEZ</span>
    </motion.span>
    <motion.span className="semana-cafe__pass semana-cafe__pass--second" variants={{ apart: { x: 12, rotate: 20 }, joined: { x: 0, rotate: 9 } }} transition={{ duration: reduced ? 0 : .8, delay: reduced ? 0 : .08, ease }}>
      <Mark kind="palmeira" /><strong>30</strong><span>DEZ</span>
    </motion.span>
  </motion.div>;
}

function Row({ row }: { row: CampaignRow }) {
  const isCombo = row.mark === 'combo';
  const reduced = useReducedMotion();
  return <motion.a href={row.eventUrl} target="_blank" rel="noopener"
    className={`semana-cafe__row semana-cafe__row--${row.accent}${isCombo ? ' semana-cafe__row--combo' : ''}`}
    aria-label={`${row.cta}: ${row.name} — ${row.venue} — ${row.date}`}
    initial={reduced ? false : { y: 20 }} whileInView={{ y: 0 }} viewport={{ once: true, amount: .15 }} transition={{ duration: reduced ? 0 : .65, ease }}>
    {!isCombo && <div className="semana-cafe__date" aria-hidden="true"><strong>{row.dateLabel.split('/')[0]}</strong><span>DEZEMBRO</span></div>}
    <div className="semana-cafe__row-inner">
      <div className="semana-cafe__row-head">
        <div className="semana-cafe__row-heading">
          {isCombo && <span className="semana-cafe__pill">{row.dateLabel}</span>}
          <h3 className="semana-cafe__name">{row.name}</h3>
        </div>
        {!isCombo && <Mark kind={row.mark as 'pre' | 'palmeira'} />}
      </div>
      <div className="semana-cafe__row-main">
        <p className="semana-cafe__lineup">{row.lineup.join(' · ')}</p>
        <p className="semana-cafe__venue"><MapPin size={14} aria-hidden="true" /><span>{row.venue}</span></p>
        <p className="semana-cafe__venue"><Clock3 size={14} aria-hidden="true" /><span>{row.date}</span></p>
      </div>
      <div className="semana-cafe__foot">
        {row.priceFrom && (
          <span className="semana-cafe__price">
            <span className="semana-cafe__price-label">A partir de</span>
            <span className="semana-cafe__price-value">{row.priceFrom}</span>
            {row.lotLabel && <span className="semana-cafe__price-lot">{row.lotLabel}</span>}
          </span>
        )}
        <span className="semana-cafe__cta"><span>{row.cta}</span><span className="semana-cafe__cta-icon"><ArrowRight size={18} aria-hidden="true" /></span></span>
      </div>
    </div>
    {isCombo && <ComboPasses />}
  </motion.a>;
}

function SemanaCafeSectionComponent({ events }: SemanaCafeSectionProps) {
  const reduced = useReducedMotion();
  const rows = buildCampaignRows(events) as CampaignRow[];
  if (!rows.length) return null;
  const nights = rows.filter((row) => row.mark !== 'combo');
  const combos = rows.filter((row) => row.mark === 'combo');
  return <section className="semana-cafe" aria-labelledby="semana-cafe-title">
    <div className="container-modern semana-cafe__body">
      <div className="semana-cafe__intro">
        <p className="semana-cafe__eyebrow">Café de La Musique · 10 anos · Maceió</p>
        <motion.h1 className="semana-cafe__headline" id="semana-cafe-title" aria-label="Seu fim de ano começa aqui."
          initial={reduced ? false : 'hidden'} whileInView="visible" viewport={{ once: true, amount: .35 }}>
          <span className="semana-cafe__line" aria-hidden="true"><motion.span variants={{ hidden: { y: '110%' }, visible: { y: 0 } }} transition={{ duration: reduced ? 0 : .75, ease }}>Seu fim de ano</motion.span></span>{' '}
          <span className="semana-cafe__line" aria-hidden="true"><motion.em variants={{ hidden: { y: '110%' }, visible: { y: 0 } }} transition={{ duration: reduced ? 0 : .8, delay: reduced ? 0 : .1, ease }}>começa aqui.</motion.em></span>
        </motion.h1>
        <p className="semana-cafe__dates"><span aria-hidden="true" />{SEMANA_CAFE.headline}</p>
        <img className="semana-cafe__wordmark" src={SEMANA_CAFE.wordmark} alt={SEMANA_CAFE.wordmarkAlt} width={668} height={740} loading="lazy" />
      </div>
      <div className="semana-cafe__rows">{[...nights, ...combos].map((row) => <Row key={row.eventId} row={row} />)}</div>
    </div>
  </section>;
}

export const SemanaCafeSection = memo(SemanaCafeSectionComponent);
